import { Component, Input, ChangeDetectionStrategy, ViewChild, EventEmitter, Output, ChangeDetectorRef, SimpleChanges, OnChanges, inject } from '@angular/core';
import { EventFeedItem } from '../../feed.model';
import { TranslateService } from '@ngx-translate/core';
import { BaseComponent } from 'app/shared/components/base/base.component';
import { EventFeedCommentModalComponent } from './comment/comment.component';
import { EventFeedService } from 'app/shared/components/feed/event-feed/event-feed.service';
import { MediaService } from 'app/services/media/media.service';
import { ModalService } from 'app/shared/components/modal';
import { FormBuilderField, customFieldToFormBuilderField } from 'app/shared/components/form-builder';
import { AppService } from 'app/app.service';
import { ExternalEventLink } from './iframe/iframe.component';
import { Router } from '@angular/router';
import { MeasurementUnitsService } from 'app/services/measurement-units/measurement-units.service';
import { MetricsService } from 'app/services/metrics/metrics.service';
import { MetricsOptionsService } from 'app/services';


@Component({
    selector: 'key-event-details',
    templateUrl: './details.component.html',
    styleUrls: ['./details.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class EventDetailsComponent extends BaseComponent implements OnChanges {

    tabIndex = 0;
    tabs: string[];

    linkTabs: ExternalEventLink[];

    @Input() showActions = true;
    @Input() isMobile: boolean;
    @Input() event: EventFeedItem;
    @Input() eventFeedService: EventFeedService;
    @Output() onClose = new EventEmitter();

    @ViewChild(EventFeedCommentModalComponent, { static: true }) commentModal: EventFeedCommentModalComponent;

    private router = inject(Router);
    private metrics = inject(MetricsService);
    private metricsOptions = inject(MetricsOptionsService);

    constructor(
        private app: AppService,
        public ref: ChangeDetectorRef,
        private media: MediaService,
        private modal: ModalService,
        public i18n: TranslateService,
        public measurement: MeasurementUnitsService,
    ) {
        super();
    }
    
    ngOnChanges(changes: SimpleChanges) {
        if (changes.event) {

            // this.event.details.links = [
            //     { title: 'Street View', url: 'https://www.google.com/maps/embed?api=1&map_action=pano&viewpoint=48.857832,2.295226&heading=-45', type: 'tab' },
            //     { title: 'Crash PDF', url: 'https://www.awesomefilm.com/script/Crash.pdf', type: 'download' },
            //     { title: 'Crash Report', url: 'https://www.awesomefilm.com/script/Crash.pdf', type: 'external' },
            //     { title: 'Admin', url: '#/admin', type: 'internal' },
            //     { title: 'Crash Analysis', url: 'https://www.keytelematics.com/', type: 'modal' },
            // ];

            this.linkTabs = (this.event?.details?.links || []).filter(x => x.type === 'tab');

            this.tabs = [
                this.i18n.instant('ALERTS.TABS.DETAILS'),
                this.i18n.instant('ALERTS.TABS.CONTACTS'),
                ...this.linkTabs.map(x => x.title),
            ];

        }
    }

    async showAcknowledgeModal(item: EventFeedItem): Promise<EventFeedItem> {

        this.captureAnalytics('acknowledge', 'start');

        const result = await this.modal.form<any>({
            groups: [{
                name: this.i18n.instant('ALERTS.BUTTONS.ACKNOWLEDGE'),
                fields: this.buildCommentForm(),
            }],
        }, {});

        if (result) {
            this.captureAnalytics('acknowledge', 'confirm');

            return this.eventFeedService.acknowledge(item.id, result).then(event => {
                this.event = event;
                this.ref.markForCheck();
                return event;
            });
        }
        return null;

    }

    async showCommentModal(item: EventFeedItem): Promise<EventFeedItem> {

        this.captureAnalytics('comment', 'start');

        const result = await this.modal.form<any>({
            groups: [{
                name: this.i18n.instant('ALERTS.BUTTONS.COMMENT'),
                fields: this.buildCommentForm(),
            }],
        }, {});

        if (result) {
            this.captureAnalytics('comment', 'confirm');

            const dateStart = item.date;
            return this.eventFeedService.comment(item.id, result, dateStart ).then(event => {
                this.event = event;
                this.ref.markForCheck();
                return event;
            });
        }
        return null;

    }

     buildCommentForm(): FormBuilderField[] {
        const customFields = this.app.client.customFields?.event || [];
        return [
            ...customFields.map(field => customFieldToFormBuilderField(field, this.measurement)),
            // only include the default comment field if the user has not overriden it in their own custom fields
            customFields.find(x => x.id === 'comment') ? null : { id: 'comment', title: this.i18n.instant('ALERTS.BUTTONS.COMMENT'), type: 'memo', required: true },
        ].filter(x => x);
    }

    async showRequestVideoModal(item: EventFeedItem): Promise<EventFeedItem> {
        this.captureVideoAnalytics({step: 'start'});
        const assetId = this.event.origin ? this.event.origin.id : this.event.details.asset && this.event.details.asset.id;
        const result = await this.media.requestVideoModalForEvent({ assetId: assetId, date: item.date });
        if (result) {
            try {
                const event = await this.media.requestVideoForEvent(item.owner.id, item.id, result.duration, result.offset, result.cameras);
                this.event = { ...this.event, media: event.media };

                this.captureVideoAnalytics({ step: 'confirm', file_name: event.media.length > 0 ? event.media[0].filename : null });

                this.ref.markForCheck();
            } catch (err) {
                this.modal.error(err.message);
            }
            
        }
        return this.event;

    }

    close() {
        this.onClose.emit();
    }

    onTabSelected(tab: string) {
        this.tabIndex = this.tabs.indexOf(tab);
        if (tab === 'Contacts') {
            this.captureAnalytics('contact_view', 'tab_click', tab);
        }
    }
    
    private captureVideoAnalytics(payload: Record<string, any>) {
        if (!this.metricsOptions.isPage('trip_history')) return;

        this.metrics.capture('video_request', {
            screen: 'trip_history',
            source: 'pop_up',
            value: `${this.event.class} ${this.event.type}`,
            ...payload,
        });
    }

    private captureAnalytics( event: 'comment' | 'acknowledge' | 'contact_view', step: 'start' | 'confirm' | 'tab_click', tab: string = '') {
        const isLiveMap = this.metricsOptions.isPage('live_map');
        const isTripHistory = this.metricsOptions.isPage('trip_history');

        if (!isLiveMap && !isTripHistory) return;

        const isAlert = this.router.url.includes('alerts');
        const screenTab = isAlert ? 'alerts' : 'events';
        const prefix = isAlert ? 'alert' : 'event';
        const eventName = `${prefix}_${event}`;

        const captureParams: Record<string, any> = {
            screen: this.metricsOptions.getScreenFromUrl(),
            tab: screenTab,
            source: 'pop_up',
            device_id: '',
        };

        if (step !== 'tab_click' || !tab) {
            captureParams.step = step;
            captureParams.value = `${this.event.class} ${this.event.type}`;
        }

        this.metrics.capture(step === 'tab_click' && tab ? event : eventName, captureParams);
    }
}
